import { FC } from 'react'
import { Stack, TextField } from '@mui/material'
import { FormatoReuniao } from '@/types/reuniao'

/**
 * Campos de agendamento de reunião — data/hora, formato (Online/Presencial) e local —
 * compartilhados entre a página Nova oportunidade (bloco "3. Agendar com o Comercial
 * Originei"), o `ReuniaoDialog` da Agenda e os fluxos futuros (A10/A15).
 *
 * Regra do Figma (frame 37:2 "Nova reunião"): local é OBRIGATÓRIO quando o formato é
 * Presencial — o backend valida (LOCAL_OBRIGATORIO_PRESENCIAL); aqui o campo já acusa
 * e `localPendente` deixa o chamador bloquear o submit antes do 400.
 */

export interface ReuniaoFormFieldsValue {
  /** Valor de `<input type="datetime-local">` (YYYY-MM-DDTHH:mm). */
  dataHora: string
  /** '' = ainda não escolhido (reuniões antigas não têm formato). */
  formato: FormatoReuniao | ''
  local: string
}

export const FORMATO_LABEL: Record<FormatoReuniao, string> = {
  ONLINE: 'Online',
  PRESENCIAL: 'Presencial',
}

/** Presencial sem local: o submit deve ficar bloqueado enquanto for `true`. */
export const localPendente = (
  v: Pick<ReuniaoFormFieldsValue, 'formato' | 'local'>
): boolean => v.formato === 'PRESENCIAL' && !v.local.trim()

interface ReuniaoFormFieldsProps {
  value: ReuniaoFormFieldsValue
  onChange: (value: ReuniaoFormFieldsValue) => void
  /**
   * `false` esconde o campo de data/hora — para formulários que já têm os seus
   * (o `ReuniaoDialog` tem Início/Término próprios e reusa só formato + local).
   */
  exibirDataHora?: boolean
  /**
   * `true` (padrão) exige a escolha do formato; `false` rotula o vazio como
   * "Não informado" — para editar reuniões anteriores à Onda 3, sem o campo.
   */
  formatoObrigatorio?: boolean
  disabled?: boolean
}

export const ReuniaoFormFields: FC<ReuniaoFormFieldsProps> = ({
  value,
  onChange,
  exibirDataHora = true,
  formatoObrigatorio = true,
  disabled = false,
}) => {
  const pendente = localPendente(value)
  return (
    <Stack spacing={2}>
      {exibirDataHora && (
        <TextField
          label="Data e hora"
          type="datetime-local"
          value={value.dataHora}
          onChange={(e) => onChange({ ...value, dataHora: e.target.value })}
          fullWidth
          required
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />
      )}
      <TextField
        label="Formato"
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        value={value.formato}
        onChange={(e) => onChange({ ...value, formato: e.target.value as FormatoReuniao | '' })}
        fullWidth
        required={formatoObrigatorio}
        disabled={disabled}
      >
        <option value="">{formatoObrigatorio ? 'Selecione…' : 'Não informado'}</option>
        {(Object.keys(FORMATO_LABEL) as FormatoReuniao[]).map((f) => (
          <option key={f} value={f}>
            {FORMATO_LABEL[f]}
          </option>
        ))}
      </TextField>
      <TextField
        label={value.formato === 'PRESENCIAL' ? 'Local' : 'Local / link'}
        value={value.local}
        onChange={(e) => onChange({ ...value, local: e.target.value })}
        placeholder={
          value.formato === 'PRESENCIAL'
            ? 'Ex.: escritório do cliente, sala 204…'
            : 'Ex.: Google Meet, Teams…'
        }
        fullWidth
        required={value.formato === 'PRESENCIAL'}
        disabled={disabled}
        error={pendente}
        helperText={pendente ? 'Obrigatório para reunião presencial.' : undefined}
      />
    </Stack>
  )
}
